/* eslint-disable no-underscore-dangle, class-methods-use-this, no-param-reassign */
import L from 'leaflet';

/**
 * Options controlling how overlapping markers are expanded ("spiderfied").
 * All distances are in screen pixels unless stated otherwise.
 * @property keepSpiderfied When true, clicking an already spiderfied marker will keep the group expanded instead of collapsing first.
 * @property nearbyDistance Radius (px) around the clicked marker used to detect "overlapping" markers.
 * @property circleSpiralSwitchover Threshold for switching layout: below uses a circle, at/above uses a spiral.
 * @property circleFootSeparation Arc-length (px) between marker endpoints on the circle layout.
 * @property circleStartAngle Initial angle (radians) for the first marker on the circle layout.
 * @property spiralFootSeparation Arc-length (px) between successive endpoints on the spiral layout.
 * @property spiralLengthStart Initial leg length (px) for the spiral layout.
 * @property spiralLengthFactor Growth factor controlling how quickly the spiral expands.
 * @property legWeight Polyline weight for the connector "legs".
 * @property legColors Colors for connector legs in usual and highlighted states.
 */
export interface SpiderfierOptions {
  keepSpiderfied?: boolean;
  nearbyDistance?: number;
  circleSpiralSwitchover?: number;
  circleFootSeparation?: number;
  circleStartAngle?: number;
  spiralFootSeparation?: number;
  spiralLengthStart?: number;
  spiralLengthFactor?: number;
  legWeight?: number;
  legColors?: {
    usual: string;
    highlighted: string;
  };
}

type OmsMarker = L.Marker & {
  _oms?: boolean;
  _omsData?: {
    usualPosition: L.LatLng;
    leg: L.Polyline;
    highlightListeners?: {
      highlight: () => void;
      unhighlight: () => void;
    };
  };
};

type Listener = (...args: any[]) => void;

/**
 * Class representing the OverlappingMarkerSpiderfier.
 * Manages groups of overlapping markers on a Leaflet map and expands them out
 * into a spider-like formation when clicked.
 */
export default class OverlappingMarkerSpiderfier {
  private map: L.Map;

  private markers: OmsMarker[] = [];

  private markerListeners: Listener[] = [];

  private listeners: Record<string, Listener[]> = {};

  /**
   * Configuration defaults (overridable via SpiderfierOptions).
   * Distances/lengths are in screen pixels; angles in radians.
   */

  /** Keep group expanded when clicking a spiderfied marker. */
  keepSpiderfied = false;

  /** Pixel radius used to consider markers as "nearby". */
  nearbyDistance = 20;

  /** Use spiral layout when marker count is >= this threshold. */
  circleSpiralSwitchover = 9;

  /** Arc-length between points on the circle layout. */
  circleFootSeparation = 25;

  /** Starting angle for the first point on the circle. */
  circleStartAngle = (Math.PI * 2) / 12;

  /** Arc-length step along the spiral between points. */
  spiralFootSeparation = 28;

  /** Initial leg length for the spiral layout. */
  spiralLengthStart = 11;

  /** Spiral growth factor controlling expansion rate. */
  spiralLengthFactor = 5;

  /** Polyline weight for connector legs. */
  legWeight = 1.5;

  /** Colors for legs: usual and highlighted. */
  legColors = { usual: '#5F6D7E', highlighted: '#214EEB' };

  private spiderfied?: boolean;

  private spiderfying?: boolean;

  private unspiderfying?: boolean;

  /**
   * Create a new OverlappingMarkerSpiderfier bound to a Leaflet map.
   * @param map Target Leaflet map instance.
   * @param opts Optional configuration overriding defaults.
   */
  constructor(map: L.Map, opts: SpiderfierOptions = {}) {
    this.map = map;
    Object.assign(this, opts);
    ['click', 'zoomend'].forEach((e) => {
      this.map.addEventListener(e, () => this.unspiderfy());
    });
  }

  private initMarkerArrays() {
    this.markers = [];
    this.markerListeners = [];
  }

  /**
   * Register a marker to be managed by the spiderfier.
   * @returns this for chaining.
   */
  addMarker(marker: OmsMarker) {
    if (marker._oms) return this;
    marker._oms = true;
    const listener = () => this.spiderListener(marker);
    marker.addEventListener('click', listener);
    this.markerListeners.push(listener);
    this.markers.push(marker);
    return this;
  }

  /** Get a shallow copy of all registered markers. */
  getMarkers(): OmsMarker[] {
    return this.markers.slice();
  }

  /**
   * Unregister a marker. If it is spiderfied, the group is first unspiderfied.
   * @returns this for chaining.
   */
  removeMarker(marker: OmsMarker) {
    if (marker._omsData) {
      this.unspiderfy();
    }
    const i = this.markers.indexOf(marker);
    if (i < 0) return this;
    const listener = this.markerListeners.splice(i, 1)[0];
    marker.removeEventListener('click', listener);
    delete marker._oms;
    this.markers.splice(i, 1);
    return this;
  }

  /**
   * Remove all managed markers and their event listeners.
   * @returns this for chaining.
   */
  clearMarkers() {
    this.unspiderfy();
    this.markers.forEach((m, i) => {
      const listener = this.markerListeners[i];
      m.removeEventListener('click', listener);
      delete m._oms;
    });
    this.initMarkerArrays();
    return this;
  }

  /** Add a custom event listener (e.g., 'spiderfy', 'unspiderfy', 'click'). */
  addListener(event: string, func: Listener) {
    (this.listeners[event] ||= []).push(func);
    return this;
  }

  /** Remove a previously added custom event listener. */
  removeListener(event: string, func: Listener) {
    const arr = this.listeners[event];
    if (!arr) return this;
    const i = arr.indexOf(func);
    if (i >= 0) arr.splice(i, 1);
    return this;
  }

  /** Remove all listeners for a given custom event. */
  clearListeners(event: string) {
    this.listeners[event] = [];
    return this;
  }

  private trigger(event: string, ...args: any[]) {
    (this.listeners[event] ?? []).forEach((f) => f(...args));
  }

  /**
   * Compute evenly spaced endpoints on a circle around a center point.
   * @param count Number of points to generate.
   * @param centerPt Center of the circle in layer coordinates.
   */
  private generatePtsCircle(count: number, centerPt: L.Point) {
    const twoPi = Math.PI * 2;
    const circumference = this.circleFootSeparation * (2 + count);
    const legLength = circumference / twoPi;
    const angleStep = twoPi / count;
    return Array.from({ length: count }, (_, i) => {
      const angle = this.circleStartAngle + i * angleStep;
      return new L.Point(
        centerPt.x + legLength * Math.cos(angle),
        centerPt.y + legLength * Math.sin(angle),
      );
    });
  }

  /**
   * Compute endpoints distributed along an outward spiral from a center point.
   * @param count Number of points to generate.
   * @param centerPt Center in layer coordinates.
   */
  private generatePtsSpiral(count: number, centerPt: L.Point) {
    let legLength = this.spiralLengthStart;
    let angle = 0;
    return Array.from({ length: count }, (_, i) => {
      angle += this.spiralFootSeparation / legLength + i * 0.0005;
      const pt = new L.Point(
        centerPt.x + legLength * Math.cos(angle),
        centerPt.y + legLength * Math.sin(angle),
      );
      legLength += (Math.PI * 2 * this.spiralLengthFactor) / angle;
      return pt;
    });
  }

  /** Internal click handler: expand (spiderfy) or forward click when applicable. */
  private spiderListener(marker: OmsMarker) {
    const markerSpiderfied = !!marker._omsData;
    if (!(markerSpiderfied && this.keepSpiderfied)) {
      this.unspiderfy();
    }
    if (markerSpiderfied) {
      this.trigger('click', marker);
      return;
    }
    const pxSq = this.nearbyDistance * this.nearbyDistance;
    const markerPt = this.map.latLngToLayerPoint(marker.getLatLng());

    const { nearbyMarkerData, nonNearbyMarkers } = this.markers
      .filter((m) => this.map.hasLayer(m))
      .reduce(
        (acc, m) => {
          const mPt = this.map.latLngToLayerPoint(m.getLatLng());
          if (this.ptDistanceSq(mPt, markerPt) < pxSq) {
            acc.nearbyMarkerData.push({ marker: m, markerPt: mPt });
          } else {
            acc.nonNearbyMarkers.push(m);
          }
          return acc;
        },
        {
          nearbyMarkerData: [] as { marker: OmsMarker; markerPt: L.Point }[],
          nonNearbyMarkers: [] as OmsMarker[],
        },
      );
    if (nearbyMarkerData.length === 1) {
      this.trigger('click', marker);
    } else {
      this.spiderfy(nearbyMarkerData, nonNearbyMarkers);
    }
  }

  /** Create mouseover/mouseout handlers to toggle leg highlight state. */
  private makeHighlightListeners(marker: OmsMarker) {
    return {
      highlight: () =>
        marker._omsData?.leg.setStyle({ color: this.legColors.highlighted }),
      unhighlight: () =>
        marker._omsData?.leg.setStyle({ color: this.legColors.usual }),
    };
  }

  /**
   * Expand a cluster of nearby markers into circle/spiral layout and draw legs.
   * Triggers the 'spiderfy' event with affected markers.
   */
  private spiderfy(
    markerData: { marker: OmsMarker; markerPt: L.Point }[],
    nonNearbyMarkers: OmsMarker[],
  ) {
    this.spiderfying = true;
    const bodyPt = this.ptAverage(markerData.map((md) => md.markerPt));
    const footPts =
      markerData.length >= this.circleSpiralSwitchover
        ? this.generatePtsSpiral(markerData.length, bodyPt).reverse()
        : this.generatePtsCircle(markerData.length, bodyPt);

    const spiderfiedMarkers: OmsMarker[] = [];

    footPts.forEach((footPt) => {
      const footLl = this.map.layerPointToLatLng(footPt);
      const nearestMarkerDatum = this.minExtract(markerData, (md) =>
        this.ptDistanceSq(md.markerPt, footPt),
      );
      const { marker } = nearestMarkerDatum;
      const leg = new L.Polyline([marker.getLatLng(), footLl], {
        color: this.legColors.usual,
        weight: this.legWeight,
        interactive: false,
      });
      this.map.addLayer(leg);
      marker._omsData = { usualPosition: marker.getLatLng(), leg };
      if (this.legColors.highlighted !== this.legColors.usual) {
        const mhl = this.makeHighlightListeners(marker);
        marker._omsData.highlightListeners = mhl;
        marker.addEventListener('mouseover', mhl.highlight);
        marker.addEventListener('mouseout', mhl.unhighlight);
      }
      marker.setLatLng(footLl);
      marker.setZIndexOffset(1000000);
      spiderfiedMarkers.push(marker);
    });

    delete this.spiderfying;
    this.spiderfied = true;
    this.trigger('spiderfy', spiderfiedMarkers, nonNearbyMarkers);
  }

  /**
   * Collapse spiderfied markers back to their usual positions.
   * Triggers the 'unspiderfy' event.
   * @returns this for chaining.
   */
  unspiderfy(markerNotToMove: OmsMarker | null = null) {
    if (!this.spiderfied) return this;
    this.unspiderfying = true;
    const unspiderfiedMarkers: OmsMarker[] = [];
    const nonNearbyMarkers: OmsMarker[] = [];

    this.markers.forEach((marker) => {
      const data = marker._omsData;
      if (data) {
        this.map.removeLayer(data.leg);
        if (marker !== markerNotToMove) {
          marker.setLatLng(data.usualPosition);
        }
        marker.setZIndexOffset(0);
        const mhl = data.highlightListeners;
        if (mhl) {
          marker.removeEventListener('mouseover', mhl.highlight);
          marker.removeEventListener('mouseout', mhl.unhighlight);
        }
        delete marker._omsData;
        unspiderfiedMarkers.push(marker);
      } else {
        nonNearbyMarkers.push(marker);
      }
    });
    delete this.unspiderfying;
    delete this.spiderfied;
    this.trigger('unspiderfy', unspiderfiedMarkers, nonNearbyMarkers);
    return this;
  }

  /** Squared Euclidean distance between two layer points (avoids sqrt). */
  private ptDistanceSq(pt1: L.Point, pt2: L.Point) {
    const dx = pt1.x - pt2.x;
    const dy = pt1.y - pt2.y;
    return dx * dx + dy * dy;
  }

  /** Arithmetic mean of a set of layer points. */
  private ptAverage(pts: L.Point[]) {
    const numPts = pts.length;
    const sum = pts.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), {
      x: 0,
      y: 0,
    });
    return new L.Point(sum.x / numPts, sum.y / numPts);
  }

  /**
   * Remove and return the item with minimum score from an array.
   * @param set Array to search (mutated: item is spliced out).
   * @param func Scoring function; lower is better.
   */
  private minExtract<T>(set: T[], func: (item: T) => number): T {
    const { index: bestIndex } = set.reduce(
      (acc, item, idx) => {
        const val = func(item);
        return val < acc.value ? { index: idx, value: val } : acc;
      },
      { index: 0, value: func(set[0]) },
    );
    return set.splice(bestIndex, 1)[0];
  }
}
